import { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Dialog, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Trophy, UploadSimple, FileText, Copy, EnvelopeSimple, Archive, DownloadSimple, PencilSimple, Check, X, ArrowClockwise, Warning } from "@phosphor-icons/react";
import { ScoreRadar, ScoreBars, IndicesBadges } from "@/components/ScoreCharts";
import UnifiedReportView from "@/components/UnifiedReportView";
import OutcomePanel from "@/components/OutcomePanel";
import { useAuth } from "@/contexts/AuthContext";
import ResizableDialogContent from "@/components/ResizableDialog";
import ConfirmDelete from "@/components/ConfirmDelete";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AREA_LABELS = {
  INI: "Iniziativa", AFF: "Affidabilita", MET: "Metodo", QUA: "Qualita", FOC: "Focus",
  ANA: "Analisi", CRE: "Creativita", REL: "Relazione", INF: "Influenza", INT: "Integrita"
};
const AREA_DESCRIPTIONS = {
  INI: "Capacita di prendere l'iniziativa e agire proattivamente",
  AFF: "Livello di affidabilita e coerenza nei comportamenti",
  MET: "Approccio metodico e strutturato al lavoro",
  QUA: "Attenzione alla qualita e cura del dettaglio",
  FOC: "Capacita di mantenere concentrazione e priorita",
  ANA: "Capacita analitica e di valutazione critica",
  CRE: "Creativita e apertura all'innovazione",
  REL: "Competenze relazionali e di networking",
  INF: "Capacita di influenza e persuasione",
  INT: "Integrita e coerenza valoriale"
};
const AREAS = ["INI","AFF","MET","QUA","FOC","ANA","CRE","REL","INF","INT"];
function getBandLabel(score) {
  if (score >= 90) return { label: "Dominante", color: "#0071e3" };
  if (score >= 75) return { label: "Solida", color: "#34c759" };
  if (score >= 60) return { label: "Promettente", color: "#ffcc00" };
  if (score >= 40) return { label: "Potenziale", color: "#ff9500" };
  return { label: "Critica", color: "#ff3b30" };
}

function formatDateIT(iso) {
  if (!iso) return "";
  const p = iso.split("T")[0].split("-");
  if (p.length === 3 && p[0].length === 4) return `${p[2]}-${p[1]}-${p[0]}`;
  return iso;
}

const STATUS_OPTIONS = ["In attesa", "In corso", "Completato", "Non idoneo"];
const STATUS_COLORS = {
  "In attesa": "bg-yellow-50 text-yellow-700",
  "In corso": "bg-blue-50 text-blue-700",
  "Completato": "bg-green-50 text-green-700",
  "Non idoneo": "bg-red-50 text-red-600",
  "Archiviato": "bg-gray-100 text-gray-500",
};

export default function CandidateDetailDialog({ candId, open, onClose, onRefresh, positions = [] }) {
  const { user } = useAuth();
  const isAdmin = !!user?.is_admin;
  const [cand, setCand] = useState(null);
  const [loading, setLoading] = useState(false);
  const [notes, setNotes] = useState("");
  const [editingNotes, setEditingNotes] = useState(false);
  const [report, setReport] = useState(null);
  const [editMode, setEditMode] = useState(false);
  const [editForm, setEditForm] = useState(null);
  const [editSaving, setEditSaving] = useState(false);
  const [resendLink, setResendLink] = useState(false);
  const [regenerating, setRegenerating] = useState(false);
  // BUG-66: report generation polling (loader + counter elapsed)
  const [genStatus, setGenStatus] = useState("idle"); // idle|generating|ready|absent|error
  const [genStartedAt, setGenStartedAt] = useState(null);
  const [genElapsed, setGenElapsed] = useState(0);
  const [genError, setGenError] = useState(null);

  const loadCand = () => {
    if (!candId) return;
    axios.get(`${API}/candidates/${candId}`, { withCredentials: true })
      .then(r => { setCand(r.data); setNotes(r.data.hr_notes || ""); })
      .catch(() => toast.error("Errore caricamento candidato"));
    // BUG-66: usa endpoint dedicato /report-status invece di scaricare tutta la lista
    axios.get(`${API}/candidates/${candId}/report-status`, { withCredentials: true })
      .then(async r => {
        const st = r.data || {};
        if (st.status === "ready" && st.report_id) {
          try {
            const res = await axios.get(`${API}/reports/${st.report_id}`, { withCredentials: true });
            setReport(res.data);
            setGenStatus("ready");
            setGenStartedAt(null);
          } catch {}
        } else if (st.status === "generating") {
          setReport(null);
          setGenStatus("generating");
          setGenStartedAt(st.started_at ? new Date(st.started_at).getTime() : Date.now());
          setGenError(null);
        } else if (st.status === "pending") {
          // Test completato ma report mai partito → kick ensure-report
          setReport(null);
          setGenStatus("generating");
          setGenStartedAt(Date.now());
          try {
            await axios.post(`${API}/candidates/${candId}/ensure-report`, {}, { withCredentials: true });
          } catch (e) {
            // Idempotente: se il report e' arrivato nel frattempo, va bene
          }
        } else if (st.status === "failed") {
          setGenStatus("error");
          setGenError(st.error || "Errore generazione report");
        } else {
          // absent: analisi non completata, niente report
          setReport(null);
          setGenStatus("absent");
        }
      }).catch(() => {});
  };

  // BUG-66: polling status + elapsed counter durante generazione
  useEffect(() => {
    if (genStatus !== "generating" || !candId) return;
    const startedTs = genStartedAt || Date.now();
    const tickElapsed = setInterval(() => {
      setGenElapsed(Math.floor((Date.now() - startedTs) / 1000));
    }, 1000);
    let cancelled = false;
    const poll = async () => {
      if (cancelled) return;
      try {
        const r = await axios.get(`${API}/candidates/${candId}/report-status`, { withCredentials: true });
        const st = r.data || {};
        if (st.status === "ready" && st.report_id) {
          const res = await axios.get(`${API}/reports/${st.report_id}`, { withCredentials: true });
          setReport(res.data);
          setGenStatus("ready");
          setGenError(null);
          // Soft refresh candidate (per ottenere needs_refresh aggiornato)
          axios.get(`${API}/candidates/${candId}`, { withCredentials: true }).then(rc => setCand(rc.data)).catch(()=>{});
          return;
        }
        if (st.status === "failed") {
          setGenStatus("error");
          setGenError(st.error || "Errore generazione report");
          return;
        }
        // Timeout difensivo a 360s (6 min) — backend p95 ~4 min, safety margin
        if ((Date.now() - startedTs) > 360000) {
          setGenStatus("error");
          setGenError("Generazione bloccata. Riprova fra qualche minuto.");
          return;
        }
        if (!cancelled) setTimeout(poll, 2000);
      } catch (e) {
        if (!cancelled) setTimeout(poll, 4000);
      }
    };
    setTimeout(poll, 2000);
    return () => { cancelled = true; clearInterval(tickElapsed); };
  }, [genStatus, candId, genStartedAt]);

  useEffect(() => {
    if (candId && open) { setLoading(true); loadCand(); setLoading(false); setEditingNotes(false); }
  }, [candId, open]);

  const refresh = () => { loadCand(); if (onRefresh) onRefresh(); };

  const updateField = async (field, value) => {
    try {
      await axios.put(`${API}/candidates/${candId}`, { [field]: value }, { withCredentials: true });
      toast.success("Aggiornato");
      refresh();
    } catch { toast.error("Errore"); }
  };

  const saveNotes = async () => {
    await updateField("hr_notes", notes);
    setEditingNotes(false);
  };

  const archive = async () => {
    // D + MIG-22: usa il nuovo POST /archive con motivo "altro" (default da scheda).
    // Per un motivo specifico l'utente può usare il dialog dalla lista candidati.
    try {
      await axios.post(`${API}/candidates/${candId}/archive`, { reason: "altro" }, { withCredentials: true });
      toast.success("Candidato archiviato");
      if (onClose) onClose();
      if (onRefresh) onRefresh();
    } catch (e) { toast.error(e?.response?.data?.detail || "Errore"); }
  };

  const copyTestLink = () => {
    if (!cand?.test_link) return;
    navigator.clipboard.writeText(`${window.location.origin}/analisi/candidato/${cand.test_link}`);
    toast.success("Link analisi copiato");
  };

  const sendEmail = async () => {
    try {
      await axios.post(`${API}/candidates/${candId}/send-test-email`, {}, { withCredentials: true });
      toast.success("Email inviata");
      refresh();
    } catch { toast.error("Errore invio email"); }
  };

  const startEdit = () => {
    setEditForm({
      first_name: cand.first_name || "",
      last_name: cand.last_name || "",
      email: cand.email || "",
      phone: cand.phone || "",
      date_of_birth: cand.date_of_birth || "",
      city: cand.city || "",
      position_id: cand.position_id || "",
    });
    setResendLink(false);
    setEditMode(true);
  };

  const cancelEdit = () => {
    setEditMode(false);
    setEditForm(null);
    setResendLink(false);
  };

  const saveEdit = async () => {
    if (!editForm) return;
    // Basic email validation
    if (editForm.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(editForm.email)) {
      toast.error("Formato email non valido");
      return;
    }
    setEditSaving(true);
    try {
      await axios.put(`${API}/candidates/${candId}`, editForm, { withCredentials: true });
      if (resendLink && editForm.email) {
        try {
          await axios.post(`${API}/candidates/${candId}/send-test-email`, {}, { withCredentials: true });
          toast.success("Dati salvati · link analisi rinviato");
        } catch { toast.warning("Dati salvati ma invio email fallito"); }
      } else {
        toast.success("Dati candidato aggiornati");
      }
      setEditMode(false);
      setEditForm(null);
      setResendLink(false);
      refresh();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Errore salvataggio");
    } finally {
      setEditSaving(false);
    }
  };

  const emailChanged = editForm && cand && editForm.email !== (cand.email || "");

  const regenerate = async (confirmMsg) => {
    if (confirmMsg && !window.confirm(confirmMsg)) return;
    setRegenerating(true);
    try {
      await axios.post(`${API}/candidates/${candId}/regenerate-report`, {}, { withCredentials: true });
      toast.success("Rigenerazione avviata");
      setReport(null);
      setGenStatus("generating");
      setGenStartedAt(Date.now());
      setGenElapsed(0);
      setGenError(null);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Errore rigenerazione");
    } finally {
      setRegenerating(false);
    }
  };

  // BUG-66: format elapsed time mm:ss
  const fmtElapsed = (s) => {
    if (!s || s < 0) return "0s";
    if (s < 60) return `${s}s`;
    const m = Math.floor(s / 60);
    const ss = s % 60;
    return `${m}m ${ss}s`;
  };

  const posName = positions.find(p => p.id === cand?.position_id)?.title || "—";

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <ResizableDialogContent defaultW={900} defaultH={700}>
        {loading && !cand ? (
          <div className="flex items-center justify-center py-12">
            <div className="animate-spin w-8 h-8 border-2 border-[#1B4FD8] border-t-transparent rounded-full" />
          </div>
        ) : cand ? (
          <>
            <DialogHeader>
              <div className="flex items-center justify-between">
                <DialogTitle className="text-lg">{cand.first_name} {cand.last_name}</DialogTitle>
                <div className="flex items-center gap-2">
                  <Button variant="outline" size="sm" onClick={copyTestLink} className="text-xs border-[#0D1F3C]/15" title="Copia link analisi">
                    <Copy size={14} className="mr-1" />Link Analisi
                  </Button>
                  {cand.email && cand.status !== "Completato" && (
                    <Button variant="outline" size="sm" onClick={sendEmail} className="text-xs border-[#1B4FD8]/30 text-[#1B4FD8]" title="Invia analisi via email">
                      <EnvelopeSimple size={14} className="mr-1" />Invia Analisi
                    </Button>
                  )}
                  <ConfirmDelete title="Archivia candidato" description={`Spostare ${cand.first_name} ${cand.last_name} in Archivio? Resta consultabile, può essere riattivato in ogni momento. Per un motivo specifico, archivia dalla lista candidati.`} onConfirm={archive}>
                    <Button variant="outline" size="sm" className="text-xs border-[#0D1F3C]/15 text-[#0D1F3C]/50" title="Sposta in Archivio (gestione chiusa)">
                      <Archive size={14} className="mr-1" />Archivia
                    </Button>
                  </ConfirmDelete>
                  {cand.scores && report && (
                    <Button variant="outline" size="sm" className="text-xs border-[#1B4FD8]/30 text-[#1B4FD8]" title="Scarica PDF completo"
                      onClick={async () => {
                        try {
                          const response = await axios.get(`${API}/reports/${report.id}/pdf`, { withCredentials: true, responseType: 'blob' });
                          const url = window.URL.createObjectURL(new Blob([response.data]));
                          const a = document.createElement('a'); a.href = url;
                          a.download = `scheda_${cand.first_name}_${cand.last_name}.pdf`;
                          document.body.appendChild(a); a.click(); a.remove();
                          window.URL.revokeObjectURL(url);
                          toast.success("PDF scaricato");
                        } catch { toast.error("Errore download PDF"); }
                      }} data-testid="download-pdf-btn">
                      <DownloadSimple size={14} className="mr-1" />PDF
                    </Button>
                  )}
                </div>
              </div>
            </DialogHeader>
            <div className="space-y-5 text-sm mt-3">
              {/* Basic Info */}
              {editMode ? (
                <div className="border border-[#1B4FD8]/30 rounded-lg p-4 bg-[#56A3F5]/5 space-y-3" data-testid="candidate-edit-form">
                  <div className="flex items-center justify-between">
                    <h4 className="text-sm font-semibold text-[#0D1F3C]">Modifica dati candidato</h4>
                    <div className="flex gap-2">
                      <Button variant="outline" size="sm" onClick={cancelEdit} disabled={editSaving} data-testid="edit-cancel-btn">
                        <X size={14} className="mr-1" />Annulla
                      </Button>
                      <Button size="sm" onClick={saveEdit} disabled={editSaving} className="bg-[#1B4FD8] hover:bg-[#0D1F3C]" data-testid="edit-save-btn">
                        <Check size={14} className="mr-1" />{editSaving ? "Salvo..." : "Salva"}
                      </Button>
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <Label className="text-xs">Nome</Label>
                      <Input value={editForm.first_name} onChange={(e) => setEditForm({ ...editForm, first_name: e.target.value })} data-testid="edit-first-name" />
                    </div>
                    <div>
                      <Label className="text-xs">Cognome</Label>
                      <Input value={editForm.last_name} onChange={(e) => setEditForm({ ...editForm, last_name: e.target.value })} data-testid="edit-last-name" />
                    </div>
                    <div>
                      <Label className="text-xs">Email</Label>
                      <Input type="email" value={editForm.email} onChange={(e) => setEditForm({ ...editForm, email: e.target.value })} data-testid="edit-email" />
                    </div>
                    <div>
                      <Label className="text-xs">Telefono</Label>
                      <Input value={editForm.phone} onChange={(e) => setEditForm({ ...editForm, phone: e.target.value })} data-testid="edit-phone" />
                    </div>
                    <div>
                      <Label className="text-xs">Data di nascita</Label>
                      <Input type="date" value={(editForm.date_of_birth || "").split("T")[0]} onChange={(e) => setEditForm({ ...editForm, date_of_birth: e.target.value })} data-testid="edit-dob" />
                    </div>
                    <div>
                      <Label className="text-xs">Citta</Label>
                      <Input value={editForm.city} onChange={(e) => setEditForm({ ...editForm, city: e.target.value })} data-testid="edit-city" />
                    </div>
                    <div className="col-span-2">
                      <Label className="text-xs">Posizione</Label>
                      <Select value={editForm.position_id} onValueChange={(v) => setEditForm({ ...editForm, position_id: v })}>
                        <SelectTrigger data-testid="edit-position"><SelectValue placeholder="Seleziona posizione" /></SelectTrigger>
                        <SelectContent>{positions.map(p => <SelectItem key={p.id} value={p.id}>{p.title}</SelectItem>)}</SelectContent>
                      </Select>
                    </div>
                  </div>
                  {emailChanged && (
                    <div className="flex items-start gap-2 p-2 rounded bg-amber-50 border border-amber-200" data-testid="email-changed-warn">
                      <input
                        type="checkbox"
                        id="resend-link-check"
                        checked={resendLink}
                        onChange={(e) => setResendLink(e.target.checked)}
                        className="mt-0.5"
                        data-testid="resend-link-check"
                      />
                      <label htmlFor="resend-link-check" className="text-xs text-amber-900 leading-relaxed">
                        L&apos;email e cambiata. Il link analisi precedente restera valido.
                        <strong> Rinvia il link analisi al nuovo indirizzo</strong> per garantire che il candidato lo riceva.
                      </label>
                    </div>
                  )}
                </div>
              ) : (
                <div className="relative">
                  <Button
                    variant="outline"
                    size="sm"
                    className="absolute -top-1 right-0 text-xs border-[#0D1F3C]/15"
                    onClick={startEdit}
                    data-testid="edit-candidate-btn"
                  >
                    <PencilSimple size={14} className="mr-1" />Modifica dati
                  </Button>
                  <div className="grid grid-cols-3 gap-3 pr-32">
                    <div><span className="text-[#0D1F3C]/50">Email:</span> {cand.email || '—'}</div>
                    <div><span className="text-[#0D1F3C]/50">Telefono:</span> {cand.phone || '—'}</div>
                    <div><span className="text-[#0D1F3C]/50">Citta:</span> {cand.city || '—'}</div>
                    <div className="flex items-center gap-2">
                      <span className="text-[#0D1F3C]/50">Stato:</span>
                      <Select value={cand.status} onValueChange={(v) => updateField("status", v)}>
                        <SelectTrigger className={`h-7 w-[130px] text-xs border-0 px-2 ${STATUS_COLORS[cand.status] || ''}`} data-testid="cand-status-select">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {STATUS_OPTIONS.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="text-[#0D1F3C]/50">Posizione:</span>
                      <Select value={cand.position_id || ""} onValueChange={(v) => updateField("position_id", v)}>
                        <SelectTrigger className="h-7 text-xs border-[#0D1F3C]/10 px-2" data-testid="cand-position-select">
                          <SelectValue placeholder="Seleziona" />
                        </SelectTrigger>
                        <SelectContent>
                          {positions.map(p => <SelectItem key={p.id} value={p.id}>{p.title}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </div>
                    <div><span className="text-[#0D1F3C]/50">Data:</span> {formatDateIT(cand.created_at)}</div>
                  </div>
                </div>
              )}

              {/* CV */}
              <div className="flex items-center gap-3 p-3 rounded-lg border border-[#0D1F3C]/8 bg-[#F4F7FB]">
                {cand.cv_filename ? (
                  <div className="flex items-center gap-2 flex-1">
                    <FileText size={20} weight="duotone" className="text-[#1B4FD8]" />
                    <span className="text-sm text-[#0D1F3C]">{cand.cv_filename}</span>
                    {cand.cv_size_bytes ? (
                      <span className="text-[10px] text-[#0D1F3C]/40">({(cand.cv_size_bytes/1024/1024).toFixed(2)}MB)</span>
                    ) : null}
                    {cand.cv_text && <Badge className="bg-green-50 text-green-700 text-[10px]">Testo estratto</Badge>}
                  </div>
                ) : (
                  <div className="flex items-center gap-2 flex-1">
                    <UploadSimple size={20} className="text-[#0D1F3C]/30" />
                    <span className="text-sm text-[#0D1F3C]/50">Nessun CV caricato</span>
                  </div>
                )}
                {cand.cv_file_id && (
                  <Button variant="outline" size="sm" data-testid="download-cv-btn"
                    onClick={async () => {
                      try {
                        const r = await axios.get(`${API}/candidates/${candId}/cv`, {
                          withCredentials: true, responseType: "blob",
                        });
                        const url = URL.createObjectURL(r.data);
                        const a = document.createElement("a");
                        a.href = url;
                        a.download = cand.cv_filename || `cv_${candId}.pdf`;
                        document.body.appendChild(a);
                        a.click();
                        a.remove();
                        URL.revokeObjectURL(url);
                      } catch { toast.error("Errore download CV"); }
                    }}>
                    <FileText size={14} className="mr-1" />Scarica CV
                  </Button>
                )}
                <label className="cursor-pointer">
                  <input type="file" accept=".pdf,.docx,.doc" className="hidden"
                    onChange={async (e) => {
                      const file = e.target.files[0];
                      if (!file) return;
                      const fd = new FormData();
                      fd.append("file", file);
                      try {
                        await axios.post(`${API}/candidates/${candId}/upload-cv`, fd, { withCredentials: true, headers: { "Content-Type": "multipart/form-data" }});
                        toast.success("CV caricato");
                        refresh();
                      } catch { toast.error("Errore upload CV"); }
                    }} />
                  <Button variant="outline" size="sm" asChild><span><UploadSimple size={14} className="mr-1" />{cand.cv_file_id ? "Sostituisci" : "Carica CV"}</span></Button>
                </label>
              </div>

              {/* Match Score */}
              {cand.match_score && (
                <div className={`p-4 rounded-xl border ${
                  cand.match_score === 'Alta' ? 'border-[#10B981]/30 bg-[#10B981]/5' :
                  cand.match_score === 'Parziale' ? 'border-[#F59E0B]/30 bg-[#F59E0B]/5' :
                  'border-[#EF4444]/30 bg-[#EF4444]/5'}`}>
                  <div className="flex items-center gap-2 mb-1">
                    <Trophy size={18} weight="duotone" className={
                      cand.match_score === 'Alta' ? 'text-[#10B981]' :
                      cand.match_score === 'Parziale' ? 'text-[#F59E0B]' : 'text-[#EF4444]'} />
                    <span className="text-sm font-semibold text-[#0D1F3C]">Compatibilita Ruolo: <span className={
                      cand.match_score === 'Alta' ? 'text-[#10B981]' :
                      cand.match_score === 'Parziale' ? 'text-[#F59E0B]' : 'text-[#EF4444]'}>{cand.match_score}</span></span>
                  </div>
                  {!cand.match_preview && cand.match_reason && (
                    <p className="text-xs text-[#0D1F3C]/70 leading-relaxed">{cand.match_reason}</p>
                  )}
                </div>
              )}

              {/* BUG-66: Loader generazione report (visibile durante generating) */}
              {genStatus === "generating" && (
                <div
                  className="flex items-center gap-3 p-4 rounded-xl border border-[#1B4FD8]/30 bg-[#1B4FD8]/5"
                  data-testid="candidate-report-generating-loader"
                >
                  <div className="animate-spin w-6 h-6 border-2 border-[#1B4FD8] border-t-transparent rounded-full flex-shrink-0" />
                  <div className="flex-1">
                    <p className="text-sm font-semibold text-[#0D1F3C]">Generazione report in corso...</p>
                    <p className="text-xs text-[#0D1F3C]/60 mt-0.5">
                      Elaborazione… <span data-testid="candidate-report-elapsed-counter" className="font-mono">{fmtElapsed(genElapsed)}</span>
                      {genElapsed > 30 && genElapsed < 90 && " · sintesi semantica in corso"}
                      {genElapsed >= 90 && " · ultimi controlli"}
                    </p>
                  </div>
                </div>
              )}
              {genStatus === "error" && (
                <div className="flex items-center gap-3 p-4 rounded-xl border border-red-300 bg-red-50" data-testid="candidate-report-error-banner">
                  <Warning size={20} weight="fill" className="text-red-600 flex-shrink-0" />
                  <div className="flex-1">
                    <p className="text-sm font-semibold text-red-700">Errore generazione report</p>
                    <p className="text-xs text-red-600 mt-0.5">{genError || "Riprova tra qualche minuto."}</p>
                  </div>
                  {isAdmin && (
                    <Button size="sm" variant="outline" onClick={() => regenerate()} className="border-red-400 text-red-700">
                      Riprova
                    </Button>
                  )}
                </div>
              )}

              {/* Regeneration banner (needs_refresh) or admin-always button */}
              {(cand.scores || report) && (
                <div className="flex items-center justify-between gap-2" data-testid="regenerate-row">
                  {cand.needs_refresh && !isAdmin ? (
                    <div className="flex-1 flex items-center gap-2 p-3 rounded-lg bg-amber-50 border border-amber-200" data-testid="needs-refresh-banner">
                      <Warning size={18} weight="fill" className="text-amber-700 flex-shrink-0" />
                      <span className="text-sm text-amber-900">
                        JD, CV o Note HR sono stati modificati dopo l&apos;analisi. Puoi ricalcolare la compatibilità con la posizione (le risposte del candidato non cambiano).
                      </span>
                    </div>
                  ) : <div />}
                  <div className="flex items-center gap-2 flex-shrink-0">
                    {cand.needs_refresh && !isAdmin && (
                      <Button
                        size="sm"
                        onClick={() => {
                          const qleft = user?.quota?.tests_limit >= 9999 ? "∞" : (user?.quota?.tests_limit - user?.quota?.tests_used);
                          regenerate(`Ricalcolare la compatibilità consuma 1 analisi dalla quota (residue: ${qleft}). Continuare?\n\nNota: vengono ricalcolati solo il match con la posizione e il report. Le risposte del candidato restano invariate.`);
                        }}
                        disabled={regenerating || genStatus === "generating"}
                        className="bg-amber-600 hover:bg-amber-700 text-white"
                        data-testid="needs-refresh-regenerate-btn"
                        title="Le risposte all'analisi del candidato sono fisse. Si ricalcola solo il match con la posizione di riferimento."
                      >
                        <ArrowClockwise size={14} className={`mr-1 ${genStatus === "generating" ? "animate-spin" : ""}`} />
                        {regenerating || genStatus === "generating" ? "In corso..." : "Ricalcola compatibilità"}
                      </Button>
                    )}
                    {isAdmin && (
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => regenerate("Admin: forzare la rigenerazione con le STYLE_RULES attuali?")}
                        disabled={regenerating || genStatus === "generating"}
                        className="border-[#1B4FD8] text-[#1B4FD8]"
                        data-testid="admin-regenerate-btn"
                        title="Admin: forza rigenerazione · non consuma quota"
                      >
                        <ArrowClockwise size={14} className={`mr-1 ${genStatus === "generating" ? "animate-spin" : ""}`} />
                        {regenerating || genStatus === "generating" ? "In corso..." : "Rigenera Report"}
                      </Button>
                    )}
                  </div>
                </div>
              )}

              {/* Unified report view (charts inline + AI sections + single disclaimer + HR notes on top) */}
              {(cand.scores || report) && (
                <UnifiedReportView
                  report={report}
                  subject={cand}
                  entityType="candidate"
                  entityId={candId}
                  onNotesUpdated={(newNotes) => setCand({ ...cand, hr_notes: newNotes })}
                />
              )}

              {/* B76 — Outcome panel: cattura esito reale post-analisi (Fase 1 ciclo chiuso). */}
              <OutcomePanel
                subjectType="candidate"
                subjectId={candId}
                hasScores={!!cand.scores}
                initialOutcome={cand.outcome || null}
              />

              {/* Download PDF button (outside report view so it stays always visible) */}
              {report?.html && (
                <div className="flex justify-end">
                  <Button variant="outline" size="sm" className="text-xs border-[#1B4FD8]/30 text-[#1B4FD8]"
                    onClick={async () => {
                      try {
                        const response = await axios.get(`${API}/reports/${report.id}/pdf`, { withCredentials: true, responseType: 'blob' });
                        const url = window.URL.createObjectURL(new Blob([response.data]));
                        const a = document.createElement('a'); a.href = url;
                        a.download = `report_${cand.first_name}_${cand.last_name}.pdf`;
                        document.body.appendChild(a); a.click(); a.remove();
                        window.URL.revokeObjectURL(url);
                        toast.success("PDF scaricato");
                      } catch { toast.error("Errore download PDF"); }
                    }} data-testid="download-full-pdf-btn">
                    <DownloadSimple size={14} className="mr-1" />Scarica PDF Completo
                  </Button>
                </div>
              )}
            </div>
          </>
        ) : null}
      </ResizableDialogContent>
    </Dialog>
  );
}
